import * as Sentry from "@sentry/react";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { Suspense, lazy, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "src/components/ErrorState";
import { SimpleLoadingScreen } from "src/components/LoadingScreen/LoadingScreen";
import { Flex } from "src/components/ui/Flex";
import { useHotjar } from "src/hooks/use-hotjar";
import { useLanguageFromUserSettings } from "src/locales/use-language-from-user-settings";
import { MatomoProvider } from "src/matomo/MatomoProvider";
import { setupMsw } from "src/mock-service-worker/setup";
import LogoutPage from "src/pages/logout-page";
import { MainRouter } from "src/router/main.router";
import {
	useIsOidcUserLoading,
	useOidc,
	useOidcUser,
} from "src/services/oidc-exporter";
import { getSettings, isProd } from "src/settings";
import { accountUsersMeRetrieve } from "src/types/_generated";
import { client } from "src/types/_generated/client.gen";
import type { LoginFailedError } from "src/types/error";

const ReactQueryDevtools = isProd()
	? () => null
	: lazy(() =>
			import("@tanstack/react-query-devtools").then((res) => ({
				default: res.ReactQueryDevtools,
			})),
		);

function App() {
	void setupMsw();
	const { oidcUser } = useOidcUser();
	const isUserLoading = useIsOidcUserLoading();
	const { t } = useTranslation(undefined, {
		keyPrefix: "oidc.noSessionAvailable",
	});
	const { isAuthenticated } = useOidc();
	const settings = getSettings();

	client.setConfig({
		baseUrl: settings.productApi.URL,
	});

	const { isLoading: isLanguageSettingsLoading } =
		useLanguageFromUserSettings({
			enabled: !!oidcUser,
		});

	useHotjar();

	// login to Zendesk
	useEffect(() => {
		if (!isUserLoading && oidcUser && window.zE) {
			window.zE(
				"messenger",
				"loginUser",
				function jwtCallback(callback: (jwt: string) => void) {
					accountUsersMeRetrieve().then((response) => {
						// FIXME: we can remove this check after we generated the types for the response
						if (response.data && "zendeskJwt" in response.data) {
							callback(response.data.zendeskJwt as string);
						}
					});
				},
				function loginCallback(error: null | LoginFailedError) {
					if (error) {
						// Example error handling
						const { type, reason, message } = error;
						Sentry.captureException(error);
						console.error(
							`Error type: ${type}, reason: ${reason}, message: ${message}`,
						);
					} else {
						console.info("User logged in to Zendesk successfully");
					}
				},
			);
		}
	}, [oidcUser, isUserLoading]);

	if (isLanguageSettingsLoading || isUserLoading) {
		return (
			<SimpleLoadingScreen
				className="h-screen border-transparent bg-pl-light-slate"
				variant="fit"
			/>
		);
	}

	/**
	 * If the user is unauthenticated they end up in sign in page within the main router
	 * If the user is authenticated but oidcUser client fails to load user or token we have to log them out manually
	 * Note: The case is reproduced when oidcUser fetch fails and gets unauthenticated response, however isAuthenticated=true and token is still valid within oidc client
	 */
	if (isAuthenticated && !oidcUser) {
		return (
			<Flex dir="col" gap={4} align="center">
				<ErrorState title={t("title")} description={t("subTitle")} />
				<LogoutPage />
			</Flex>
		);
	}

	return (
		<MatomoProvider>
			<NuqsAdapter>
				<MainRouter isAuthenticated={isAuthenticated} />
				<Suspense>
					<ReactQueryDevtools initialIsOpen={false} />
				</Suspense>
			</NuqsAdapter>
		</MatomoProvider>
	);
}

export { App };
